import {
  AfterViewInit, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef,
  inject, input, OnChanges, OnDestroy, OnInit, output, PLATFORM_ID, signal, viewChild, ViewEncapsulation
} from '@angular/core';

import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { isPlatformBrowser, LowerCasePipe } from '@angular/common';

import { fromEvent, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import {
  CheckoutEnum, DeviceService, IconSizeEnum, IPromo, IWarrantyInformations, PositionEnum, SdBorderWrapperComponent,
  SdButtonComponent, SdConvertedPriceComponent, SdFormCheckboxComponent, SdModalDefaultComponent,
  SdProductPromoComponent, SdHtmlComponent
} from '@seb/web-design-system/core';

import { SdDividedPaymentComponent } from '@seb/web-design-system/payment';

import type { ICheckoutTotal } from './checkout-total.interface';
import { CheckoutStepsEnum } from '../checkout-steps.enum';

@Component({
  selector: 'sd-checkout-total',
  templateUrl: './checkout-total.component.html',
  styleUrls: ['./checkout-total.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    SdBorderWrapperComponent,
    SdProductPromoComponent,
    SdFormCheckboxComponent,
    SdButtonComponent,
    SdDividedPaymentComponent,
    SdModalDefaultComponent,
    RouterOutlet,
    LowerCasePipe,
    TranslateModule,
    SdHtmlComponent,
    SdConvertedPriceComponent
  ]
})
export class SdCheckoutTotalComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {
  step = input<CheckoutStepsEnum>();
  readonly = input<boolean>();
  displayShippingFees = input(true);
  displayPaymentFees = input(false);
  isShippingFree = input(false);
  proceedDisabled = input(false);
  checkoutTotalData = input<ICheckoutTotal>();
  extraLegals = input(false);
  modalNoteTitleLevel = input(3);
  guestCheckout = input(true);
  isPaypal = input(false);
  paypalCheckout = input(true);
  isApplePay = input(false);
  isSellingRepairPackage = input(false);
  buttonProperties = input<string>();
  promos = input<IPromo[]>();
  totalProducts = input<number>(0);
  warrantyInformation = input<IWarrantyInformations>();
  isExpressCheckout = input(false);
  isExpressOrderReview = input(false);

  divided = output<void>();
  checkout = output<CheckoutEnum>();
  promoClick = output<IPromo>();
  termsChecked = output<boolean>();

  readonly checkboxTerms = viewChild<SdFormCheckboxComponent>('checkboxTerms');
  readonly extraLegalsEl = viewChild<ElementRef>('extraLegals');

  private readonly platformId = inject(PLATFORM_ID);
  private readonly deviceService = inject(DeviceService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  isMobile = signal(false);

  readonly checkboxTermsEl = computed(() => this.checkboxTerms()?.elementRef);
  titleLevel = computed(() => this.isMobile() ? 4 : 3);
  textPosition = computed(() => this.isMobile() ? this.positionEnum.left : this.positionEnum.center);

  positionEnum = PositionEnum;
  iconSizeEnum = IconSizeEnum;
  checkoutEnum = CheckoutEnum;
  checkoutStepsEnum = CheckoutStepsEnum;
  subscriptions = new Subscription();
  termsAccepted = false;
  isOpened = false;
  mandatoryCheck = false;
  legalTermsTranslation: string;

  ngOnChanges() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        window['KlarnaOnsiteService'] = window['KlarnaOnsiteService'] || [];
        window['KlarnaOnsiteService'].push({ eventName: 'refresh-placements' });
      }, 100);
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile.set(x);
      })
    );

    this.legalTermsTranslation = this.isSellingRepairPackage()
      ? 'checkout.total.repairPackageLegalTerms'
      : 'checkout.total.legalTerms';
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.checkboxTermsEl()) {
        this.transformLinksToHandleModal(this.checkboxTermsEl());
      }

      if (this.extraLegals()) {
        this.transformLinksToHandleModal(this.extraLegalsEl());
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  transformLinksToHandleModal(element: ElementRef) {
    setTimeout(() => {
      const links = element.nativeElement.querySelectorAll('a:not([target])');
      for (const link of links) {
        this.subscriptions.add(
          fromEvent(link, 'click').subscribe((event: PointerEvent) => {
            event.preventDefault();
            this.toggleModal(link.getAttribute('href'));
          }),
        );
      }
    }, 500);
  }

  toggleModal(route: string) {
    this.isOpened = true;
    this.router.navigate([this.router?.url, { outlets: { modal: route } }], {
      skipLocationChange: true
    });
  }

  updateForms() {
    this.termsAccepted = this.checkboxTerms().value;
    this.termsChecked.emit(this.termsAccepted);
  }

  checkTerms(target: CheckoutEnum) {
    if ((this.step() === this.checkoutStepsEnum.payments || this.isExpressOrderReview()) && !this.termsAccepted) {
      this.toggleModalNote();
    } else {
      this.checkout.emit(target);
    }
  }

  onDividedClick() {
    this.divided.emit();
  }

  toggleModalNote() {
    this.mandatoryCheck = !this.mandatoryCheck;
  }

  onPromoClick(promo: IPromo) {
    this.promoClick.emit(promo);
  }

  onClose() {
    this.router.navigate([this.router?.url, { outlets: { modal: null } }], {
      relativeTo: this.activatedRoute.parent
    });
  }
}
